import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GenderClient, GenderDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new GenderClient(API_BASE_URL, axiosInstance);

// Query hook - Get all genders for dropdowns (cached forever)
export const useGenders = () => {
  return useQuery({
    queryKey: ['genders'],
    queryFn: async () => {
      const response = await apiClient.searchGenders({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useGenderSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchGenders({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single gender by ID
export const useGenderById = (id: string) => {
  return useQuery({
    queryKey: ['genders', id],
    queryFn: async () => {
      const response = await apiClient.getGenderById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create gender
export const useCreateGender = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: GenderDTO) => {
      await apiClient.createGender(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genders'] });
    },
  });
};

// Mutation hook - Update gender
export const useUpdateGender = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: GenderDTO }) => {
      await apiClient.updateGender(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genders'] });
    },
  });
};

// Mutation hook - Delete gender
export const useDeleteGender = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteGender(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genders'] });
    },
  });
};